import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ArrowLeft,
  ArrowUpRight,
  Plus,
  Minus,
  Check,
  X as XIcon,
  Instagram,
  Youtube,
  Music2,
  Twitter,
  Users2,
  Heart,
  MessageSquare,
  Share2,
  Eye,
  BarChart3,
  Sparkles,
  Scissors,
  Target,
  Lightbulb,
  Mic,
  Share,
  Video,
  TrendingUp,
  Play,
} from 'lucide-react';
import ZeditHeader from '../components/zedit/ZeditHeader';
import ZeditFooter from '../components/zedit/ZeditFooter';

const GRAD = 'linear-gradient(135deg, #C8FD6A 0%, #91ED52 50%, #63E140 100%)';
const ACCENT_INK = '#C8FD6A';

const ClipsPage = () => {
  useEffect(() => {
    document.title = 'Clips by Zedit | Web3 Short-Form Content & Distribution';
  }, []);

  return (
    <div className="zedit-theme glass-theme min-h-screen bg-black text-[var(--glass-text)] relative overflow-hidden">
      {/* Subtle radial green glows · accent only */}
      <div className="glass-orb" style={{ width: '500px', height: '500px', background: '#76E84B', top: '-160px', left: '-160px', opacity: 0.18 }} />
      <div className="glass-orb" style={{ width: '600px', height: '600px', background: '#63E140', top: '40%', right: '-260px', opacity: 0.14 }} />
      <div className="glass-orb" style={{ width: '500px', height: '500px', background: '#91ED52', bottom: '-180px', left: '20%', opacity: 0.16 }} />

      <div className="relative z-10">
        <ZeditHeader theme="dark" />
        <main>
          <ClipsHero />
          <ShortFormShowcase />
          <FeaturesBento />
          <StartingPointSection />
          <ProcessSection />
          <WhyClipsSection />
          <PlatformsSection />
          <ComparisonSection />
          <ClipsFAQ />
          <ClipsCTA />
        </main>
        <ZeditFooter theme="dark" />
      </div>
    </div>
  );
};

/* ─────────────────────── HERO ─────────────────────── */
const ClipsHero = () => (
  <section className="relative pt-32 sm:pt-40 lg:pt-48 pb-24 sm:pb-32">
    <div className="container mx-auto px-5 sm:px-8 relative text-center">
      <Link
        to="/"
        data-testid="back-to-zedit"
        className="inline-flex items-center gap-1.5 text-xs text-[var(--glass-text-muted)] hover:text-[var(--glass-text)] mb-10 transition-colors"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        Back to Zedit
      </Link>

      <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full glass-panel text-sm font-medium mb-12 sm:mb-16">
        <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: '#63E140', boxShadow: '0 0 10px #63E140' }} />
        <span className="text-[var(--glass-text)]">The most consumed form of content is Short Form Content</span>
      </div>

      <motion.h1
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        data-testid="clips-hero-headline"
        className="font-display text-[44px] leading-[1.02] sm:text-7xl lg:text-[100px] xl:text-[120px] max-w-[1100px] mx-auto tracking-tight text-[var(--glass-text)]"
      >
        Achieve{' '}
        <span
          className="bg-clip-text text-transparent"
          style={{ backgroundImage: 'linear-gradient(135deg, #C8FD6A 0%, #91ED52 50%, #63E140 100%)' }}
        >
          Mindshare
        </span>{' '}
        Through Strategic Clip Distribution
      </motion.h1>

      <motion.p
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.15 }}
        className="mt-10 text-base sm:text-lg lg:text-xl font-light text-[var(--glass-text-soft)] max-w-2xl mx-auto leading-relaxed"
      >
        We help Web3 founders dominate social feeds with quality content that captures attention
        and builds authentic brands.
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.25 }}
        className="mt-12"
      >
        <a
          href="#starting-point"
          data-testid="clips-hero-cta-primary"
          className="inline-flex items-center justify-center gap-2 px-9 py-5 rounded-full text-white font-semibold text-base hover:scale-105 transition-transform"
          style={{ background: GRAD, boxShadow: '0 14px 36px -8px rgba(118,232,75,0.6)' }}
        >
          Get Started
          <ArrowRight className="w-4 h-4" />
        </a>
      </motion.div>
    </div>
  </section>
);

/* ─────────────────── SHORT-FORM SHOWCASE ─────────────────── */
const SFC_THUMBS = [
  {
    src: 'https://customer-assets.emergentagent.com/job_fb684b19-cb6a-4dd0-9eb3-cce9aac29bc7/artifacts/pjqeebuw_sfc%20thumbnail%201.jpg',
    handle: '@coin.gainz',
    title: 'Why this cycle is different',
    views: '2.1M',
    likes: '184K',
    platform: 'TikTok',
    Icon: Music2,
    rotate: -6,
    y: 12,
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_fb684b19-cb6a-4dd0-9eb3-cce9aac29bc7/artifacts/0m95i6ww_sfc%20thumbnail%202.jpg',
    handle: '@web3.deepdive',
    title: 'Founders explain the L2 thesis',
    views: '3.4M',
    likes: '221K',
    platform: 'YouTube Shorts',
    Icon: Youtube,
    rotate: 0,
    y: 0,
    trending: true,
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_fb684b19-cb6a-4dd0-9eb3-cce9aac29bc7/artifacts/a8xjhu97_sfc%20thumbnail%203.jpg',
    handle: '@crypto.daily',
    title: 'Reading the chart before you buy',
    views: '1.4M',
    likes: '96K',
    platform: 'Reels',
    Icon: Instagram,
    rotate: 6,
    y: 12,
  },
];

const ShortFormShowcase = () => {
  const [ref, inView] = useInView({ threshold: 0.15, triggerOnce: true });
  return (
    <section ref={ref} className="relative -mt-6 sm:-mt-10 pb-16 sm:pb-24">
      <div className="container mx-auto px-5 sm:px-8">
        <div className="relative max-w-5xl mx-auto">
          {/* subtle backdrop glow */}
          <div className="absolute inset-x-10 top-10 h-56 rounded-[48px] blur-3xl opacity-40 pointer-events-none"
               style={{ background: 'radial-gradient(closest-side, #63E140 0%, transparent 70%)' }} />

          <div className="relative flex items-end justify-center gap-4 sm:gap-6 lg:gap-8">
            {SFC_THUMBS.map((t, i) => (
              <motion.div
                key={t.handle}
                initial={{ opacity: 0, y: 40 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.7, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                style={{ transform: `rotate(${t.rotate}deg) translateY(${t.y}px)` }}
                className={`relative ${i === 1 ? 'z-20 w-[180px] sm:w-[220px] lg:w-[260px]' : 'z-10 w-[150px] sm:w-[180px] lg:w-[220px]'}`}
              >
                <div
                  className="relative rounded-[26px] sm:rounded-[32px] bg-black overflow-hidden shadow-[0_28px_60px_-18px_rgba(118,232,75,0.35),0_20px_40px_rgba(0,0,0,0.6)]"
                  style={{
                    border: i === 1 ? `1.5px solid ${ACCENT_INK}66` : '1px solid rgba(255,255,255,0.08)',
                  }}
                >
                  {/* Phone notch */}
                  <div className="absolute top-2 left-1/2 -translate-x-1/2 w-16 h-1 rounded-full bg-white/10 z-30" />

                  <div className="aspect-[9/16] relative">
                    <img src={t.src} alt={t.title} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/70" />

                    {/* Platform pill */}
                    <div className="absolute top-4 left-3 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-black/60 backdrop-blur-sm text-[9px] sm:text-[10px] font-bold text-white">
                      <t.Icon className="w-2.5 h-2.5" /> {t.platform}
                    </div>

                    {/* Trending badge */}
                    {t.trending && (
                      <div
                        className="absolute top-4 right-3 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[9px] sm:text-[10px] font-bold text-black"
                        style={{ background: GRAD }}
                      >
                        <TrendingUp className="w-2.5 h-2.5" /> TRENDING
                      </div>
                    )}

                    {/* Center play */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <motion.div
                        animate={{ scale: [1, 1.08, 1] }}
                        transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
                        className="w-11 h-11 sm:w-14 sm:h-14 rounded-full flex items-center justify-center backdrop-blur-md"
                        style={{ background: 'rgba(255,255,255,0.9)', boxShadow: '0 8px 24px rgba(0,0,0,0.4)' }}
                      >
                        <Play className="w-4 h-4 sm:w-5 sm:h-5 text-black fill-black ml-0.5" />
                      </motion.div>
                    </div>

                    {/* Bottom overlay */}
                    <div className="absolute bottom-0 inset-x-0 p-3 sm:p-4">
                      <div className="text-[10px] sm:text-[11px] font-semibold text-white/80 mb-1">{t.handle}</div>
                      <div className="text-[11px] sm:text-sm font-bold text-white leading-tight mb-2 line-clamp-2">{t.title}</div>
                      <div className="flex items-center justify-between text-white">
                        <span className="text-[10px] sm:text-xs font-bold flex items-center gap-1">
                          <Eye className="w-3 h-3" /> {t.views}
                        </span>
                        <span className="text-[10px] sm:text-xs font-bold flex items-center gap-1">
                          <Heart className="w-3 h-3 fill-current" style={{ color: '#ff5a7a' }} /> {t.likes}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Floating stat chips */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="absolute -top-6 left-2 sm:-left-6 px-3 py-2 rounded-2xl glass-panel-strong flex items-center gap-2 z-30"
          >
            <BarChart3 className="w-4 h-4" style={{ color: ACCENT_INK }} />
            <div>
              <div className="text-[9px] uppercase tracking-widest text-[var(--glass-text-muted)]">Total reach</div>
              <div className="text-sm font-black" style={{ color: ACCENT_INK }}>1B+ views</div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="absolute -bottom-4 right-2 sm:-right-4 px-3 py-2 rounded-2xl glass-panel-strong flex items-center gap-2 z-30"
          >
            <Scissors className="w-4 h-4" style={{ color: ACCENT_INK }} />
            <span className="text-xs font-bold text-[var(--glass-text)]">412 clips live</span>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/* ─────────────────── 2x2 BENTO FEATURES ─────────────────── */
const FeaturesBento = () => {
  const [ref, inView] = useInView({ threshold: 0.05, triggerOnce: true });
  return (
    <section ref={ref} className="py-12 sm:py-20">
      <div className="container mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-5 sm:gap-6">
          <BentoCard inView={inView} delay={0}><StandOutCard /></BentoCard>
          <BentoCard inView={inView} delay={0.08}><EnterpriseInsightsCard /></BentoCard>
          <BentoCard inView={inView} delay={0.16}><DataBackedCard /></BentoCard>
          <BentoCard inView={inView} delay={0.24}><EverywhereCard /></BentoCard>
        </div>
      </div>
    </section>
  );
};

const BentoCard = ({ children, inView, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    animate={inView ? { opacity: 1, y: 0 } : {}}
    transition={{ duration: 0.6, delay }}
    className="glass-panel-strong rounded-3xl p-6 sm:p-8 lg:p-10 min-h-[460px] flex flex-col"
  >
    {children}
  </motion.div>
);

const StandOutCard = () => (
  <>
    <h3 className="font-display text-2xl sm:text-3xl mb-3 text-[var(--glass-text)]">Stand out at scale</h3>
    <p className="text-sm sm:text-base font-light text-[var(--glass-text-soft)] leading-relaxed mb-8 max-w-md">
      Flood social feeds with hundreds of clips so your brand dominates the conversation.
    </p>
    <div className="space-y-3 mt-auto">
      {[
        { n: 'Instagram', Icon: Instagram, bg: 'bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400', dot: 'rgb(59, 130, 246)' },
        { n: 'YouTube Shorts', Icon: Youtube, bg: 'bg-red-500', dot: 'rgb(239, 68, 68)' },
        { n: 'TikTok', Icon: Music2, bg: 'bg-black', dot: 'rgb(34, 211, 238)' },
      ].map(({ n, Icon, bg, dot }) => (
        <div key={n} className="flex items-center justify-between gap-3 px-4 py-3.5 rounded-2xl glass-panel">
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${bg}`}>
              <Icon className="w-4 h-4 text-white" />
            </div>
            <span className="text-base font-semibold text-[var(--glass-text)]">{n}</span>
          </div>
          <span className="w-4 h-4 rounded-full border-2" style={{ borderColor: dot }}>
            <span className="block w-full h-full rounded-full" style={{ background: dot, opacity: 0.5 }} />
          </span>
        </div>
      ))}
    </div>
  </>
);

const EnterpriseInsightsCard = () => (
  <>
    <h3 className="font-display text-2xl sm:text-3xl mb-3 text-[var(--glass-text)]">Enterprise Insights</h3>
    <p className="text-sm sm:text-base font-light text-[var(--glass-text-soft)] leading-relaxed mb-8 max-w-md">
      See what clips perform best, double down on winning formats, and track results in real time.
    </p>
    <div className="rounded-2xl glass-panel p-5 mt-auto">
      <div className="flex items-center gap-5 mb-4 text-xs">
        <div className="flex items-center gap-2">
          <span className="w-3.5 h-3.5 rounded-full bg-orange-400" />
          <span className="text-[var(--glass-text)] font-semibold">Your Brand</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3.5 h-3.5 rounded-full" style={{ background: '#63E140' }} />
          <span className="text-[var(--glass-text-soft)]">crypto_clips</span>
        </div>
      </div>
      <svg viewBox="0 0 400 80" className="w-full h-16 mb-5">
        <path
          d="M0,55 C40,52 80,48 120,42 C160,35 200,30 240,26 C280,22 320,18 360,15 C380,13 400,12 400,12"
          stroke="#2f9a1f"
          strokeWidth="2.5"
          fill="none"
        />
      </svg>
      <div className="grid grid-cols-3 gap-x-5 gap-y-3 text-xs">
        {['Total Views', 'Shares', 'Comments', 'Likes', 'Top Clips', 'Trending'].map((l) => (
          <div key={l} className="flex items-center justify-between text-[var(--glass-text-soft)]">
            <span>{l}</span>
            <TrendingUp className="w-3 h-3" style={{ color: ACCENT_INK }} />
          </div>
        ))}
      </div>
    </div>
  </>
);

const DataBackedCard = () => (
  <>
    <h3 className="font-display text-2xl sm:text-3xl mb-3 text-[var(--glass-text)]">Data-backed distribution</h3>
    <p className="text-sm sm:text-base font-light text-[var(--glass-text-soft)] leading-relaxed mb-6 max-w-md">
      Every campaign comes with transparent reporting: views, clicks, and conversions you can act on.
    </p>
    <div className="rounded-2xl glass-panel p-5 mb-4">
      <div className="flex items-center justify-between mb-4">
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md border text-xs font-semibold"
              style={{ borderColor: ACCENT_INK, color: ACCENT_INK }}>
          See Growth
        </span>
        <div className="flex gap-3 text-[11px] text-[var(--glass-text-soft)]">
          <span>Monthly</span>
          <span>24hrs</span>
        </div>
      </div>
      <svg viewBox="0 0 400 100" className="w-full h-20">
        <defs>
          <linearGradient id="dbg-area" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#63E140" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#63E140" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path
          d="M0,80 C40,70 80,60 120,55 C160,50 200,42 240,35 C280,28 320,28 360,22 C380,18 400,18 400,18 L400,100 L0,100 Z"
          fill="url(#dbg-area)"
        />
        <path
          d="M0,80 C40,70 80,60 120,55 C160,50 200,42 240,35 C280,28 320,28 360,22 C380,18 400,18 400,18"
          stroke="#2f9a1f"
          strokeWidth="2.5"
          fill="none"
        />
      </svg>
    </div>
    <div className="rounded-2xl glass-panel p-4 mt-auto">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-semibold text-[var(--glass-text)]">Track what matters</span>
      </div>
      <ul className="space-y-2">
        {['Track views over time', 'Compare likes & shares', 'Spot top-performing clips'].map((t) => (
          <li key={t} className="flex items-center gap-2 text-sm font-medium text-[var(--glass-text)]">
            <span className="w-4 h-4 rounded flex items-center justify-center" style={{ background: GRAD }}>
              <Check className="w-3 h-3 text-white" />
            </span>
            {t}
          </li>
        ))}
      </ul>
    </div>
  </>
);

const EverywhereCard = () => (
  <>
    <h3 className="font-display text-2xl sm:text-3xl mb-3 text-[var(--glass-text)]">Everywhere your audience scrolls</h3>
    <p className="text-sm sm:text-base font-light text-[var(--glass-text-soft)] leading-relaxed mb-6 max-w-md">
      Flood social media with your brand that drives brand awareness.
    </p>
    <div className="rounded-2xl glass-panel p-5 mt-auto">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-4 text-xs">
        <div className="inline-flex items-center gap-1.5">
          <BarChart3 className="w-3.5 h-3.5" style={{ color: ACCENT_INK }} />
          <span className="text-[var(--glass-text)] font-semibold">Total Reach</span>
        </div>
        <span className="text-[var(--glass-text-soft)]">Last 24hrs</span>
      </div>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-5 text-xs text-[var(--glass-text-soft)]">
        <span className="inline-flex items-center gap-1"><Heart className="w-3.5 h-3.5 text-pink-500" /> Likes</span>
        <span className="inline-flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5 text-cyan-500" /> Comments</span>
        <span className="inline-flex items-center gap-1"><Share2 className="w-3.5 h-3.5 text-blue-500" /> Shares</span>
      </div>
      <div className="grid grid-cols-2 gap-2.5">
        {[
          { n: 'Reels', Icon: Instagram, bg: 'bg-gradient-to-br from-purple-500 to-pink-500' },
          { n: 'TikTok', Icon: Music2, bg: 'bg-black' },
          { n: 'Shorts', Icon: Youtube, bg: 'bg-red-500' },
          { n: 'X/Twitter', Icon: Twitter, bg: 'bg-black' },
        ].map(({ n, Icon, bg }) => (
          <div key={n} className="flex items-center gap-2.5 px-3 py-3 rounded-xl glass-panel">
            <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${bg}`}>
              <Icon className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="text-sm font-semibold text-[var(--glass-text)]">{n}</span>
          </div>
        ))}
      </div>
    </div>
  </>
);

/* ─────────────────── Choose Your Starting Point ─────────────────── */
const StartingPointSection = () => {
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });
  const cards = [
    {
      tag: 'Connect & Clip',
      title: 'Creator Collabs',
      desc: "You're ready but don't have content yet. We book weekly podcasters and AMA hosts in Web3 to create content for you, then turn it into clips.",
      bullets: ['Weekly creator bookings', 'Web3 podcast appearances', 'AMA session content', 'Consistent clip pipeline'],
      iconBg: 'bg-gradient-to-br from-blue-400 to-blue-600',
      Icon: Mic,
    },
    {
      tag: 'Just Clip',
      title: 'Clip & Distribute',
      desc: "You're ready to start clipping and can provide the content. We handle editing and strategic distribution across all platforms.",
      bullets: ['Expert clip editing', 'Multi-platform distribution', 'Algorithm optimization', 'Performance tracking'],
      iconBg: '',
      iconBgStyle: { background: GRAD },
      Icon: Scissors,
    },
  ];
  return (
    <section ref={ref} id="starting-point" className="py-24 sm:py-32">
      <div className="container mx-auto px-5 sm:px-8">
        <div className="text-center mb-16 sm:mb-20 max-w-3xl mx-auto">
          <span className="text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: ACCENT_INK }}>
            Where Are You?
          </span>
          <h2 className="font-display mt-4 text-4xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.02] text-[var(--glass-text)]">
            Choose Your Starting Point
          </h2>
          <p className="mt-6 text-base sm:text-lg font-light text-[var(--glass-text-soft)]">
            We meet you where you are. Select the option that best describes your situation.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-5 sm:gap-6 max-w-6xl mx-auto">
          {cards.map((card, i) => (
            <motion.div
              key={card.title}
              initial={{ opacity: 0, y: 24 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: i * 0.1, duration: 0.6 }}
              className="glass-panel-strong rounded-3xl p-8 sm:p-10 flex flex-col"
            >
              <div
                className={`w-20 h-20 rounded-2xl ${card.iconBg} flex items-center justify-center mb-8 shadow-xl`}
                style={card.iconBgStyle}
              >
                <card.Icon className="w-9 h-9 text-white" strokeWidth={2.5} />
              </div>
              <span
                className="inline-flex self-start items-center px-4 py-1.5 rounded-full border text-sm font-semibold mb-6"
                style={{ borderColor: ACCENT_INK, color: ACCENT_INK, background: 'rgba(118,232,75,0.12)' }}
              >
                {card.tag}
              </span>
              <h3 className="font-display text-3xl sm:text-4xl mb-5 text-[var(--glass-text)]">{card.title}</h3>
              <p className="text-sm sm:text-base font-light text-[var(--glass-text-soft)] leading-relaxed mb-8">{card.desc}</p>
              <ul className="space-y-3 mb-10">
                {card.bullets.map((b) => (
                  <li key={b} className="flex items-center gap-3 text-base font-medium text-[var(--glass-text)]">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: ACCENT_INK }} />
                    {b}
                  </li>
                ))}
              </ul>
              <a
                href="mailto:hi@zedit.io"
                data-testid={`starting-${card.title.toLowerCase().replace(/\s+/g, '-')}`}
                className="mt-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full glass-panel text-[var(--glass-text)] text-sm font-semibold hover:bg-white/30 transition-colors"
              >
                Select This Option
              </a>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─────────────────── Our Process ─────────────────── */
const ProcessSection = () => {
  const [ref, inView] = useInView({ threshold: 0.08, triggerOnce: true });
  const steps = [
    { n: '01', Icon: Lightbulb, title: 'Content Strategy', desc: "We work with you to identify the stories that resonate. Whether it's your founding journey, product updates, or industry insights." },
    { n: '02', Icon: Mic, title: 'Content Sourcing', desc: 'Provide your own footage or let us book you with Web3 podcast hosts and AMA creators to generate fresh content.' },
    { n: '03', Icon: Scissors, title: 'Editing & Optimization', desc: 'Our editors transform raw footage into platform-perfect clips. Each piece is crafted for maximum engagement on its target platform.' },
    { n: '04', Icon: Share, title: 'Strategic Distribution', desc: 'We deploy clips through our network, letting the algorithm match your content with Web3 audiences primed to engage.' },
  ];
  return (
    <section ref={ref} className="py-24 sm:py-32">
      <div className="container mx-auto px-5 sm:px-8">
        <div className="text-center mb-16 sm:mb-20 max-w-3xl mx-auto">
          <span className="text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: ACCENT_INK }}>
            Our Process
          </span>
          <h2 className="font-display mt-4 text-4xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.02] text-[var(--glass-text)]">
            From Concept to Viral Clips
          </h2>
          <p className="mt-6 text-base sm:text-lg font-light text-[var(--glass-text-soft)]">
            We handle the entire journey: strategy, content sourcing, editing, and distribution.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 max-w-7xl mx-auto">
          {steps.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 24 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: i * 0.08, duration: 0.5 }}
              className="glass-panel-strong rounded-3xl p-7 min-h-[380px] flex flex-col"
            >
              <div className="flex items-start justify-between mb-10">
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center"
                  style={{ background: 'rgba(118,232,75,0.18)', border: `1px solid ${ACCENT_INK}55` }}
                >
                  <s.Icon className="w-6 h-6" style={{ color: ACCENT_INK }} strokeWidth={2.2} />
                </div>
                <span
                  className="font-display text-5xl sm:text-6xl"
                  style={{
                    color: 'transparent',
                    WebkitTextStroke: `1.5px ${ACCENT_INK}`,
                    opacity: 0.5,
                  }}
                >
                  {s.n}
                </span>
              </div>
              <ArrowRight className="w-7 h-7 mb-6" style={{ color: ACCENT_INK }} strokeWidth={2} />
              <h3 className="font-display text-2xl mb-3 text-[var(--glass-text)]">{s.title}</h3>
              <p className="text-sm font-light text-[var(--glass-text-soft)] leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─────────────────── Why Clips by Zedit ─────────────────── */
const WhyClipsSection = () => {
  const [ref, inView] = useInView({ threshold: 0.05, triggerOnce: true });
  const features = [
    { Icon: Video, t: 'Content That Converts', d: 'Quality is everything. We create founder-led content that builds trust and turns viewers into community members.' },
    { Icon: Mic, t: 'Creator Network', d: 'Access our network of Web3 podcasters and AMA hosts who can help you create authentic, engaging content.' },
    { Icon: Users2, t: 'Community Building', d: 'Build genuine connections with your audience through consistent, high-quality short-form content.' },
    { Icon: Scissors, t: 'Expert Clip Editing', d: 'Our editors know what works. We transform long-form content into scroll-stopping clips optimized for each platform.' },
    { Icon: Target, t: 'Algorithm-Powered Reach', d: 'We work with the algorithm, not against it. Matching your content with the exact Web3 audiences who need to see it.' },
    { Icon: TrendingUp, t: 'Performance Tracking', d: "Real-time analytics and insights so you always know what's working and can double down on winning formats." },
  ];
  return (
    <section ref={ref} className="py-24 sm:py-32">
      <div className="container mx-auto px-5 sm:px-8">
        <div className="text-center mb-16 sm:mb-20 max-w-3xl mx-auto">
          <span className="text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: ACCENT_INK }}>
            Why Clips by Zedit
          </span>
          <h2 className="font-display mt-4 text-4xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.02] text-[var(--glass-text)]">
            Content Creation Meets Distribution
          </h2>
          <p className="mt-6 text-base sm:text-lg font-light text-[var(--glass-text-soft)] max-w-2xl mx-auto">
            Quality content is the foundation of every successful campaign. We don&apos;t just clip:
            we create, edit, and strategically distribute.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 max-w-7xl mx-auto">
          {features.map((f, i) => (
            <motion.div
              key={f.t}
              initial={{ opacity: 0, y: 18 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: i * 0.06 }}
              className="relative glass-panel-strong rounded-3xl p-7 sm:p-8 min-h-[260px] overflow-hidden"
            >
              <f.Icon
                className="absolute top-5 right-5 w-16 h-16 sm:w-20 sm:h-20 pointer-events-none"
                style={{ color: 'rgba(63,138,43,0.18)' }}
                strokeWidth={1.5}
              />
              <div
                className="relative w-12 h-12 rounded-xl flex items-center justify-center mb-10"
                style={{ background: 'rgba(118,232,75,0.22)', border: `1px solid ${ACCENT_INK}55` }}
              >
                <f.Icon className="w-5 h-5" style={{ color: ACCENT_INK }} strokeWidth={2.2} />
              </div>
              <h3 className="relative font-display text-xl mb-3 text-[var(--glass-text)]">{f.t}</h3>
              <p className="relative text-sm font-light text-[var(--glass-text-soft)] leading-relaxed">{f.d}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─────────────────── Multi-Platform Distribution ─────────────────── */
const PlatformsSection = () => {
  const [ref, inView] = useInView({ threshold: 0.08, triggerOnce: true });
  const platforms = [
    { n: 'TikTok', Icon: Music2, desc: 'Reach crypto-curious audiences on the platform where trends ignite and communities form.' },
    { n: 'Instagram Reels', Icon: Instagram, desc: "Connect with Web3 builders and investors through Instagram's powerful discovery algorithm." },
    { n: 'YouTube Shorts', Icon: Youtube, desc: "Capture attention in YouTube's fastest-growing format, perfect for educational Web3 content." },
    { n: 'X (Twitter)', Icon: Twitter, desc: 'The home of crypto Twitter. Our clips spread through the feeds where your community lives.' },
  ];
  return (
    <section ref={ref} className="py-24 sm:py-32">
      <div className="container mx-auto px-5 sm:px-8">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <span className="text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: ACCENT_INK }}>
            Where Web3 Lives
          </span>
          <h2 className="font-display mt-4 text-4xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.02] text-[var(--glass-text)]">
            Multi-Platform Distribution
          </h2>
          <p className="mt-6 text-base sm:text-lg font-light text-[var(--glass-text-soft)]">
            Your clips reach crypto-native audiences across every platform where they spend time.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {platforms.map((p, i) => (
            <motion.div
              key={p.n}
              initial={{ opacity: 0, y: 16 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: i * 0.08 }}
              className="glass-panel-strong rounded-3xl p-6"
            >
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
                style={{ background: 'rgba(118,232,75,0.22)', border: `1px solid ${ACCENT_INK}55` }}
              >
                <p.Icon className="w-5 h-5" style={{ color: ACCENT_INK }} />
              </div>
              <h3 className="font-display text-lg mb-2 text-[var(--glass-text)]">{p.n}</h3>
              <p className="text-sm font-light text-[var(--glass-text-soft)] leading-relaxed">{p.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─────────────────── Comparison ─────────────────── */
const ComparisonSection = () => {
  const [ref, inView] = useInView({ threshold: 0.05, triggerOnce: true });
  const us = [
    'Creator collaborations for content sourcing',
    'Access to Web3 podcasters and AMA hosts',
    'Algorithm-optimized clip distribution',
    'Authentic, founder-led storytelling',
    'Multi-platform reach and analytics',
  ];
  const them = [
    'Require you to provide all raw content',
    'Generic stock footage and templates',
    'No creator network or collaborations',
    'Pay-per-click ads with low engagement',
    'Cookie-cutter content that gets ignored',
  ];
  return (
    <section ref={ref} className="py-24 sm:py-32">
      <div className="container mx-auto px-5 sm:px-8">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <span className="text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: ACCENT_INK }}>
            The Difference
          </span>
          <h2 className="font-display mt-4 text-4xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.02] text-[var(--glass-text)]">
            Why Founders Choose Us
          </h2>
        </div>
        <div className="grid md:grid-cols-2 gap-5 sm:gap-6 max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="glass-panel-strong rounded-3xl p-8 border-2"
            style={{ borderColor: ACCENT_INK }}
          >
            <div className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.18em] mb-5" style={{ color: ACCENT_INK }}>
              <Sparkles className="w-3.5 h-3.5" /> Clips by Zedit
            </div>
            <ul className="space-y-3.5">
              {us.map((u) => (
                <li key={u} className="flex items-start gap-3 text-base font-medium text-[var(--glass-text)]">
                  <span className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: GRAD }}>
                    <Check className="w-3 h-3 text-white" />
                  </span>
                  {u}
                </li>
              ))}
            </ul>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 24 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="glass-panel rounded-3xl p-8"
          >
            <div className="inline-flex items-center text-xs font-bold uppercase tracking-[0.18em] text-[var(--glass-text-muted)] mb-5">
              Traditional Agencies
            </div>
            <ul className="space-y-3.5">
              {them.map((t) => (
                <li key={t} className="flex items-start gap-3 text-base text-[var(--glass-text-soft)]">
                  <span className="w-5 h-5 rounded-full bg-white/40 border border-[var(--glass-border)] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <XIcon className="w-3 h-3 text-[var(--glass-text-muted)]" />
                  </span>
                  {t}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/* ─────────────────── FAQ ─────────────────── */
const ClipsFAQ = () => {
  const [open, setOpen] = useState(0);
  const [ref, inView] = useInView({ threshold: 0.08, triggerOnce: true });
  const faqs = [
    { q: 'What makes Clips by Zedit different?', a: "We're not just a clipping service. We're a full content partner. We can source content through our network of Web3 podcasters and AMA hosts, or work with footage you provide. Quality content is the foundation of every successful campaign." },
    { q: 'Do I need to provide my own content?', a: "It's your choice. You can bring existing footage, or we can help you create content through our network of Web3 creators, podcasters, and AMA hosts." },
    { q: 'How does the distribution work?', a: 'We leverage social algorithms that excel at matching content with the right interest groups. Your clips reach engaged Web3 audiences organically through strategic distribution.' },
    { q: 'Can you connect me with Web3 creators?', a: 'Absolutely. We have relationships with established podcasters and AMA hosts in the Web3 space. We can book appearances that generate great content and expand your reach through trusted voices.' },
    { q: 'What platforms do you distribute on?', a: 'We focus on TikTok, Instagram Reels, YouTube Shorts, and X/Twitter. The platforms where Web3 communities are most active and engaged.' },
  ];
  return (
    <section ref={ref} id="faq" className="py-24 sm:py-32">
      <div className="container mx-auto px-5 sm:px-8 max-w-3xl">
        <div className="text-center mb-14">
          <span className="text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: ACCENT_INK }}>
            Questions Answered
          </span>
          <h2 className="font-display mt-4 text-4xl sm:text-6xl tracking-tight leading-[1.02] text-[var(--glass-text)]">
            Frequently Asked Questions
          </h2>
        </div>
        <div className="space-y-3">
          {faqs.map((f, i) => (
            <motion.div
              key={f.q}
              initial={{ opacity: 0, y: 12 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: i * 0.05 }}
              className="glass-panel rounded-2xl overflow-hidden"
            >
              <button
                onClick={() => setOpen(open === i ? -1 : i)}
                data-testid={`clips-faq-q-${i}`}
                className="w-full flex items-center justify-between gap-4 p-5 sm:p-6 text-left text-[var(--glass-text)]"
              >
                <span className="text-base sm:text-lg font-semibold">{f.q}</span>
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{
                    background: open === i ? GRAD : 'rgba(255,255,255,0.4)',
                    color: open === i ? '#fff' : 'var(--glass-text)',
                  }}
                >
                  {open === i ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                </div>
              </button>
              {open === i && (
                <div className="px-5 sm:px-6 pb-5 sm:pb-6 text-sm font-light text-[var(--glass-text-soft)] leading-relaxed">
                  {f.a}
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─────────────────── CTA ─────────────────── */
const ClipsCTA = () => (
  <section className="py-24 sm:py-32 relative">
    <div className="container mx-auto px-5 sm:px-8">
      <div className="glass-panel-strong rounded-[32px] sm:rounded-[40px] p-10 sm:p-16 lg:p-20 text-center relative overflow-hidden">
        <div className="glass-orb" style={{ width: '300px', height: '300px', background: '#76E84B', top: '-80px', left: '12%', opacity: 0.5 }} />
        <div className="glass-orb" style={{ width: '320px', height: '320px', background: '#63E140', bottom: '-100px', right: '10%', opacity: 0.5 }} />

        <div className="relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-panel text-sm font-medium mb-8">
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: '#63E140' }} />
            <span className="text-[var(--glass-text)]">Start Creating Today</span>
          </div>
          <h2 className="font-display text-4xl sm:text-6xl lg:text-[100px] tracking-tight leading-[1.02] text-[var(--glass-text)] max-w-4xl mx-auto">
            Ready to Capture{' '}
            <span
              className="bg-clip-text text-transparent"
              style={{ backgroundImage: 'linear-gradient(135deg, #C8FD6A, #63E140)' }}
            >
              Mindshare?
            </span>
          </h2>
          <p className="mt-8 text-base sm:text-lg font-light text-[var(--glass-text-soft)] max-w-2xl mx-auto">
            Let&apos;s create content that cuts through the noise and reaches the Web3 audiences that matter.
            From creator collabs to distribution, we handle everything.
          </p>
          <div className="mt-12 flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="mailto:hi@zedit.io?subject=Clips%20Strategy%20Call"
              data-testid="clips-cta-primary"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-white font-semibold text-base hover:scale-105 transition-transform"
              style={{ background: GRAD, boxShadow: '0 12px 32px -8px rgba(118,232,75,0.6)' }}
            >
              Book Strategy Call
              <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href="#faq"
              data-testid="clips-cta-secondary"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full glass-panel text-[var(--glass-text)] font-semibold text-base hover:bg-white/30 transition-colors"
            >
              View FAQ
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default ClipsPage;
